/**
 * Film Comps Validation Utilities
 * Provides validation functions for comp data and user inputs
 */

import { 
  validateIMDBId, 
  ERROR_CODES 
} from '../../types/compsTypes';

/**
 * Validate film ID input
 * @param {string} filmId - Film ID to validate
 * @returns {Object} - Validation result
 */
export const validateFilmIdInput = (filmId) => {
  if (!filmId || typeof filmId !== 'string') {
    return {
      isValid: false,
      error: 'Film ID is required',
      errorCode: ERROR_CODES.VALIDATION_ERROR
    };
  }
  
  const trimmed = filmId.trim();
  if (trimmed.length === 0) {
    return {
      isValid: false,
      error: 'Film ID cannot be empty',
      errorCode: ERROR_CODES.VALIDATION_ERROR
    };
  }
  
  if (!validateIMDBId(trimmed)) {
    return {
      isValid: false,
      error: 'Invalid IMDB ID format. Must be in format tt1234567',
      errorCode: ERROR_CODES.INVALID_FILM_ID
    };
  }
  
  return {
    isValid: true,
    error: null,
    errorCode: null
  };
};

/**
 * Validate comp assignment input
 * @param {Object} assignment - Assignment data to validate
 * @returns {Object} - Validation result
 */
export const validateCompAssignmentInput = (assignment) => {
  if (!assignment || typeof assignment !== 'object') {
    return {
      isValid: false,
      error: 'Assignment data is required',
      errorCode: ERROR_CODES.VALIDATION_ERROR
    };
  }
  
  // Validate primary film ID
  const primaryFilmValidation = validateFilmIdInput(assignment.primaryFilmId);
  if (!primaryFilmValidation.isValid) {
    return {
      isValid: false,
      error: `Primary film ID: ${primaryFilmValidation.error}`,
      errorCode: primaryFilmValidation.errorCode
    };
  }
  
  // Validate comp film ID
  const compFilmValidation = validateFilmIdInput(assignment.compFilmId);
  if (!compFilmValidation.isValid) {
    return {
      isValid: false,
      error: `Comp film ID: ${compFilmValidation.error}`,
      errorCode: compFilmValidation.errorCode
    };
  }
  
  // Check if films are the same
  if (assignment.primaryFilmId === assignment.compFilmId) {
    return {
      isValid: false,
      error: 'Primary film and comp film cannot be the same',
      errorCode: ERROR_CODES.VALIDATION_ERROR
    };
  }
  
  // Validate reason
  if (!assignment.reason || typeof assignment.reason !== 'string') {
    return {
      isValid: false,
      error: 'Assignment reason is required',
      errorCode: ERROR_CODES.VALIDATION_ERROR
    };
  }
  
  const trimmedReason = assignment.reason.trim();
  if (trimmedReason.length === 0) {
    return {
      isValid: false,
      error: 'Assignment reason cannot be empty',
      errorCode: ERROR_CODES.VALIDATION_ERROR
    };
  }
  
  if (trimmedReason.length < 10) {
    return {
      isValid: false,
      error: 'Assignment reason must be at least 10 characters long',
      errorCode: ERROR_CODES.VALIDATION_ERROR
    };
  }
  
  if (trimmedReason.length > 500) {
    return {
      isValid: false,
      error: 'Assignment reason cannot exceed 500 characters',
      errorCode: ERROR_CODES.VALIDATION_ERROR
    };
  }
  
  // Validate assigned by
  if (!assignment.assignedBy || typeof assignment.assignedBy !== 'string') {
    return {
      isValid: false,
      error: 'Assigned by field is required',
      errorCode: ERROR_CODES.VALIDATION_ERROR
    };
  }
  
  const trimmedAssignedBy = assignment.assignedBy.trim();
  if (trimmedAssignedBy.length === 0) {
    return {
      isValid: false,
      error: 'Assigned by field cannot be empty',
      errorCode: ERROR_CODES.VALIDATION_ERROR
    };
  }
  
  return {
    isValid: true,
    error: null,
    errorCode: null
  };
};

/**
 * Validate bulk assignment input
 * @param {Array} assignments - Array of assignments to validate
 * @param {string} assignedBy - User ID of the assigner
 * @returns {Object} - Validation result
 */
export const validateBulkAssignmentInput = (assignments, assignedBy) => {
  if (!Array.isArray(assignments)) {
    return {
      isValid: false,
      error: 'Assignments must be an array',
      errorCode: ERROR_CODES.VALIDATION_ERROR
    };
  }
  
  if (assignments.length === 0) {
    return {
      isValid: false,
      error: 'At least one assignment is required',
      errorCode: ERROR_CODES.VALIDATION_ERROR
    };
  }
  
  if (assignments.length > 50) {
    return {
      isValid: false,
      error: 'Cannot process more than 50 assignments at once',
      errorCode: ERROR_CODES.VALIDATION_ERROR
    };
  }
  
  // Validate assigned by
  if (!assignedBy || typeof assignedBy !== 'string' || assignedBy.trim().length === 0) {
    return {
      isValid: false,
      error: 'Assigned by field is required',
      errorCode: ERROR_CODES.VALIDATION_ERROR
    };
  }
  
  // Validate each assignment
  const errors = [];
  const validAssignments = [];
  
  assignments.forEach((assignment, index) => {
    const validation = validateCompAssignmentInput({
      ...assignment,
      assignedBy
    });
    
    if (!validation.isValid) {
      errors.push({
        index,
        error: validation.error,
        errorCode: validation.errorCode
      });
    } else {
      validAssignments.push(assignment);
    }
  });
  
  if (errors.length > 0) {
    return {
      isValid: false,
      error: `Validation failed for ${errors.length} assignment(s)`,
      errorCode: ERROR_CODES.VALIDATION_ERROR,
      details: errors
    };
  }
  
  return {
    isValid: true,
    error: null,
    errorCode: null,
    validAssignments
  };
};

/**
 * Validate comp update input
 * @param {Object} updates - Update data to validate
 * @returns {Object} - Validation result
 */
export const validateCompUpdateInput = (updates) => {
  if (!updates || typeof updates !== 'object') {
    return {
      isValid: false,
      error: 'Update data is required',
      errorCode: ERROR_CODES.VALIDATION_ERROR
    };
  }
  
  const hasReason = updates.hasOwnProperty('reason');
  const hasIsActive = updates.hasOwnProperty('isActive');
  
  if (!hasReason && !hasIsActive) {
    return {
      isValid: false,
      error: 'At least one field (reason or isActive) must be provided',
      errorCode: ERROR_CODES.VALIDATION_ERROR
    };
  }
  
  // Validate reason if provided
  if (hasReason) {
    if (typeof updates.reason !== 'string') {
      return {
        isValid: false,
        error: 'Reason must be a string',
        errorCode: ERROR_CODES.VALIDATION_ERROR
      };
    }
    
    const trimmedReason = updates.reason.trim();
    if (trimmedReason.length === 0) {
      return {
        isValid: false,
        error: 'Reason cannot be empty',
        errorCode: ERROR_CODES.VALIDATION_ERROR
      };
    }
    
    if (trimmedReason.length < 10) {
      return {
        isValid: false,
        error: 'Reason must be at least 10 characters long',
        errorCode: ERROR_CODES.VALIDATION_ERROR
      };
    }
    
    if (trimmedReason.length > 500) {
      return {
        isValid: false,
        error: 'Reason cannot exceed 500 characters',
        errorCode: ERROR_CODES.VALIDATION_ERROR
      };
    }
  }
  
  // Validate isActive if provided
  if (hasIsActive && typeof updates.isActive !== 'boolean') {
    return {
      isValid: false,
      error: 'isActive must be a boolean',
      errorCode: ERROR_CODES.VALIDATION_ERROR
    };
  }
  
  return {
    isValid: true,
    error: null,
    errorCode: null
  };
};

/**
 * Validate comp ID input
 * @param {string} compId - Comp ID to validate
 * @returns {Object} - Validation result
 */
export const validateCompIdInput = (compId) => {
  if (!compId || typeof compId !== 'string') {
    return {
      isValid: false,
      error: 'Comp ID is required',
      errorCode: ERROR_CODES.VALIDATION_ERROR
    };
  }
  
  const trimmed = compId.trim();
  if (trimmed.length === 0) {
    return {
      isValid: false,
      error: 'Comp ID cannot be empty',
      errorCode: ERROR_CODES.VALIDATION_ERROR
    };
  }
  
  // Basic format validation (should start with 'comp_' and have alphanumeric characters)
  if (!/^comp_[a-zA-Z0-9_-]+$/.test(trimmed)) {
    return {
      isValid: false,
      error: 'Invalid comp ID format',
      errorCode: ERROR_CODES.INVALID_COMP_ID
    };
  }
  
  return {
    isValid: true,
    error: null,
    errorCode: null
  };
};

/**
 * Validate search query input
 * @param {string} query - Search query to validate
 * @returns {Object} - Validation result
 */
export const validateSearchQueryInput = (query) => {
  if (!query || typeof query !== 'string') {
    return {
      isValid: false,
      error: 'Search query is required',
      errorCode: ERROR_CODES.VALIDATION_ERROR
    };
  }
  
  const trimmed = query.trim();
  if (trimmed.length === 0) {
    return {
      isValid: false,
      error: 'Search query cannot be empty',
      errorCode: ERROR_CODES.VALIDATION_ERROR
    };
  }
  
  if (trimmed.length < 2) {
    return {
      isValid: false,
      error: 'Search query must be at least 2 characters long',
      errorCode: ERROR_CODES.VALIDATION_ERROR
    };
  }
  
  if (trimmed.length > 100) {
    return {
      isValid: false,
      error: 'Search query cannot exceed 100 characters',
      errorCode: ERROR_CODES.VALIDATION_ERROR
    };
  }
  
  return {
    isValid: true,
    error: null,
    errorCode: null
  };
};

/**
 * Validate pagination parameters
 * @param {Object} params - Pagination parameters
 * @returns {Object} - Validation result
 */
export const validatePaginationParams = (params) => {
  if (!params || typeof params !== 'object') {
    return {
      isValid: false,
      error: 'Pagination parameters are required',
      errorCode: ERROR_CODES.VALIDATION_ERROR
    };
  }
  
  const { limit, offset } = params;
  
  // Validate limit
  if (limit !== undefined) {
    if (typeof limit !== 'number' || isNaN(limit)) {
      return {
        isValid: false,
        error: 'Limit must be a number',
        errorCode: ERROR_CODES.VALIDATION_ERROR
      };
    }
    
    if (limit < 1) {
      return {
        isValid: false,
        error: 'Limit must be at least 1',
        errorCode: ERROR_CODES.VALIDATION_ERROR
      };
    }
    
    if (limit > 100) {
      return {
        isValid: false,
        error: 'Limit cannot exceed 100',
        errorCode: ERROR_CODES.VALIDATION_ERROR
      };
    }
  }
  
  // Validate offset
  if (offset !== undefined) {
    if (typeof offset !== 'number' || isNaN(offset)) {
      return {
        isValid: false,
        error: 'Offset must be a number',
        errorCode: ERROR_CODES.VALIDATION_ERROR
      };
    }
    
    if (offset < 0) {
      return {
        isValid: false,
        error: 'Offset cannot be negative',
        errorCode: ERROR_CODES.VALIDATION_ERROR
      };
    }
  }
  
  return {
    isValid: true,
    error: null,
    errorCode: null
  };
};

/**
 * Validate comp type filter
 * @param {string} type - Comp type to validate
 * @returns {Object} - Validation result
 */
export const validateCompTypeFilter = (type) => {
  if (type === undefined || type === null) {
    return {
      isValid: true,
      error: null,
      errorCode: null
    };
  }
  
  if (typeof type !== 'string') {
    return {
      isValid: false,
      error: 'Comp type must be a string',
      errorCode: ERROR_CODES.VALIDATION_ERROR
    };
  }
  
  const validTypes = ['modeled', 'greenlight', 'all'];
  if (!validTypes.includes(type)) {
    return {
      isValid: false,
      error: `Invalid comp type. Must be one of: ${validTypes.join(', ')}`,
      errorCode: ERROR_CODES.VALIDATION_ERROR
    };
  }
  
  return {
    isValid: true,
    error: null,
    errorCode: null
  };
};

/**
 * Validate timeline alignment parameters
 * @param {Object} params - Timeline alignment parameters
 * @returns {Object} - Validation result
 */
export const validateTimelineAlignmentParams = (params) => {
  if (!params || typeof params !== 'object') {
    return {
      isValid: false,
      error: 'Timeline alignment parameters are required',
      errorCode: ERROR_CODES.VALIDATION_ERROR
    };
  }
  
  const { daysUntilRelease, weeksUntilRelease } = params;
  
  // At least one parameter must be provided
  if (daysUntilRelease === undefined && weeksUntilRelease === undefined) {
    return {
      isValid: true, // Both undefined is valid (no alignment)
      error: null,
      errorCode: null
    };
  }
  
  // Validate daysUntilRelease
  if (daysUntilRelease !== undefined) {
    if (typeof daysUntilRelease !== 'number' || isNaN(daysUntilRelease)) {
      return {
        isValid: false,
        error: 'Days until release must be a number',
        errorCode: ERROR_CODES.VALIDATION_ERROR
      };
    }
    
    if (daysUntilRelease < 0) {
      return {
        isValid: false,
        error: 'Days until release cannot be negative',
        errorCode: ERROR_CODES.VALIDATION_ERROR
      };
    }
  }
  
  // Validate weeksUntilRelease
  if (weeksUntilRelease !== undefined) {
    if (typeof weeksUntilRelease !== 'number' || isNaN(weeksUntilRelease)) {
      return {
        isValid: false,
        error: 'Weeks until release must be a number',
        errorCode: ERROR_CODES.VALIDATION_ERROR
      };
    }
    
    if (weeksUntilRelease < 0) {
      return {
        isValid: false,
        error: 'Weeks until release cannot be negative',
        errorCode: ERROR_CODES.VALIDATION_ERROR
      };
    }
  }
  
  return {
    isValid: true,
    error: null,
    errorCode: null
  };
};

/**
 * Validate API response structure
 * @param {Object} response - API response to validate
 * @param {string} expectedDataType - Expected data type for validation
 * @returns {Object} - Validation result
 */
export const validateAPIResponse = (response, expectedDataType = 'generic') => {
  if (!response || typeof response !== 'object') {
    return {
      isValid: false,
      error: 'Invalid response format',
      errorCode: ERROR_CODES.VALIDATION_ERROR
    };
  }
  
  if (typeof response.success !== 'boolean') {
    return {
      isValid: false,
      error: 'Response must have a success field',
      errorCode: ERROR_CODES.VALIDATION_ERROR
    };
  }
  
  if (!response.success) {
    return {
      isValid: false,
      error: response.error?.message || 'API request failed',
      errorCode: response.error?.code || ERROR_CODES.INTERNAL_ERROR
    };
  }
  
  if (!response.data) {
    return {
      isValid: false,
      error: 'Response must have data field',
      errorCode: ERROR_CODES.VALIDATION_ERROR
    };
  }
  
  return {
    isValid: true,
    error: null,
    errorCode: null
  };
};

/**
 * Sanitize user input
 * @param {string} input - Input string to sanitize
 * @returns {string} - Sanitized string
 */
export const sanitizeInput = (input) => {
  if (typeof input !== 'string') return '';
  
  return input
    .trim()
    .replace(/[<>]/g, '') // Remove potential HTML tags
    .replace(/javascript:/gi, '') // Remove javascript: protocol
    .replace(/on\w+=/gi, ''); // Remove event handlers
};

/**
 * Validate and sanitize text input
 * @param {string} input - Text input to validate and sanitize
 * @param {Object} options - Validation options
 * @param {number} options.minLength - Minimum length
 * @param {number} options.maxLength - Maximum length
 * @param {boolean} options.allowEmpty - Whether empty input is allowed
 * @returns {Object} - Validation result with sanitized input
 */
export const validateAndSanitizeTextInput = (input, options = {}) => {
  const { minLength = 0, maxLength = 1000, allowEmpty = false } = options;
  
  if (typeof input !== 'string') {
    return {
      isValid: false,
      error: 'Input must be a string',
      errorCode: ERROR_CODES.VALIDATION_ERROR,
      sanitized: ''
    };
  }
  
  const sanitized = sanitizeInput(input);
  
  if (!allowEmpty && sanitized.length === 0) {
    return {
      isValid: false,
      error: 'Input cannot be empty',
      errorCode: ERROR_CODES.VALIDATION_ERROR,
      sanitized
    };
  }
  
  if (sanitized.length < minLength) {
    return {
      isValid: false,
      error: `Input must be at least ${minLength} characters long`,
      errorCode: ERROR_CODES.VALIDATION_ERROR,
      sanitized
    };
  }
  
  if (sanitized.length > maxLength) {
    return {
      isValid: false,
      error: `Input cannot exceed ${maxLength} characters`,
      errorCode: ERROR_CODES.VALIDATION_ERROR,
      sanitized
    };
  }
  
  return {
    isValid: true,
    error: null,
    errorCode: null,
    sanitized
  };
};

// Export all validation functions
const compValidators = {
  validateFilmIdInput,
  validateCompAssignmentInput,
  validateBulkAssignmentInput,
  validateCompUpdateInput,
  validateCompIdInput,
  validateSearchQueryInput,
  validatePaginationParams,
  validateCompTypeFilter,
  validateTimelineAlignmentParams,
  validateAPIResponse,
  sanitizeInput,
  validateAndSanitizeTextInput
};

export default compValidators;
